import { useEffect, useRef } from 'react';
import type { Line } from 'd3-shape';
import { line } from 'd3-shape';
import { first, isNil, isNumber, last, map, max, times } from 'lodash-es';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import type { AlarmPoint } from '@/graphql/types';
import { useProfile } from 'assets/js/profile';
import { getDataType } from '../../../graph/data-types';
import { DATA_TYPE } from '../../../graph/enums';
import type { Range } from '../../../graph/axis';
import { Graph } from '../../../graph/graph';

// Type for an alarm with its points and optional alarm level index
export interface Alarm {
    points: AlarmPoint[];
    alarmLevelIndex?: number | null;
}

// Type for line coordinate [vkar, freq]
type LineCoordinate = [number, number];

// Type for rendered line data
interface RenderedLine {
    points: LineCoordinate[];
    cssClass: string;
}

// 25 refers to number_of_freq_to_vkar_items in spectrum.py
export function createEmptyAlarmPoints(): AlarmPoint[] {
    return times(25, () => ({ freq: null, vkar: null }));
}

const INITIAL_AXIS_DOMAIN: Range = [0, 15];

export function calculateYAxisMax(maxVkar: number): number {
    return maxVkar * 1.2;
}

class CustomAlarmGraph extends Graph {
    alarms: Alarm[] = [];

    createXAxisDomain$() {
        return of(INITIAL_AXIS_DOMAIN);
    }

    createYAxisDomain$() {
        return of(INITIAL_AXIS_DOMAIN);
    }

    createXAxisText$(): Observable<string> {
        return getDataType(DATA_TYPE.FDOM).getAxisTitle$({ profile: this.profile });
    }

    createYAxisText$(): Observable<string> {
        return getDataType(DATA_TYPE.VTOP).getAxisTitle$({ profile: this.profile });
    }

    updateMaxOnScales(): void {
        const vkars = this.alarms.flatMap((alarm) => map(alarm.points, 'vkar'));
        const freqs = this.alarms.flatMap((alarm) => map(alarm.points, 'freq'));
        const maxVkar = max(vkars);
        const maxFreq = max(freqs);

        if (!maxVkar || !maxFreq) {
            return;
        }
        this.xAxis.scale.domain([0, maxFreq > 350 ? 350 : maxFreq * 1.2]);
        this.yAxis.scale.domain([0, calculateYAxisMax(maxVkar)]);
    }

    updateAlarms(alarms: Alarm[]): void {
        this.alarms = alarms;
        this.updateMaxOnScales();
        this.render();
    }

    cleanupLine(dirtyPoints: AlarmPoint[]): LineCoordinate[] {
        const lineMakesSense = dirtyPoints.some(({ vkar }) => isNumber(vkar) && vkar > 0);

        if (!lineMakesSense) {
            return [];
        }

        const points: LineCoordinate[] = dirtyPoints
            .filter(
                (point): point is { vkar: number; freq: number } =>
                    isNumber(point.vkar) && isNumber(point.freq)
            )
            .map((point) => [point.vkar, point.freq]);

        // Do some visual enhancement of the line.
        if (points.length !== 0) {
            const [firstVkar, firstFreq] = first(points)!;
            const [lastVkar, lastFreq] = last(points)!;

            if (firstFreq !== 0) {
                // Prepend line with a point at 0hz and the vkar
                // of the first point in the original line.
                points.unshift([firstVkar, 0]);
            }

            // Create a flat line leaving the screen with the last vkar.
            points.push([lastVkar, lastFreq * 1.2]);
        }

        return points;
    }

    renderAlarmLines(): void {
        const lines: RenderedLine[] = this.alarms.map((alarm) => ({
            // Cleanup line:
            points: this.cleanupLine(alarm.points),
            cssClass: !isNil(alarm.alarmLevelIndex)
                ? `alarmLine alarmLine${alarm.alarmLevelIndex}`
                : 'catLine usedLine',
        }));

        const drawLine: Line<LineCoordinate> = line<LineCoordinate>()
            .x((d) => this.xAxis.scale(d[1]))
            .y((d) => this.yAxis.scale(d[0]));

        // Render lines.
        this.drawArea
            .selectAll('.catLine, .alarmLine')
            .data(lines)
            .join('svg:path')
            .attr('class', (d) => d.cssClass)
            .attr('d', (d) => drawLine(d.points));
    }

    render(): void {
        this.renderAlarmLines();
    }
}

const margin = {
    top: 20,
    right: 0,
    bottom: 40,
    left: 55,
};

interface AlarmLinesGraphProps {
    alarms: Alarm[];
}

export function AlarmLinesGraph({ alarms }: AlarmLinesGraphProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const graphInstanceRef = useRef<CustomAlarmGraph | null>(null);
    const profile = useProfile();

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        if (!graphInstanceRef.current) {
            graphInstanceRef.current = new CustomAlarmGraph({
                profile,
                margin,
                name: 'bar',
                buildContainer: true,
                container: containerRef.current,
                containerHeight: 300,
                containerWidth: 400,
                containerClass: 'graph',
            });
        }
    }, [profile]);

    useEffect(() => {
        const instance = graphInstanceRef.current;
        if (!instance) {
            return undefined;
        }

        let cancelled = false;

        void instance.ready.then(() => {
            if (!cancelled) {
                instance.updateAlarms(alarms);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [alarms]);

    return <div ref={containerRef} />;
}
